import { X, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useLanguageStore } from '../../stores/language-store';
import { t } from '../../i18n/translations';

interface PromptPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  finalPrompt: string;
}

export function PromptPreviewDialog({ open, onClose, finalPrompt }: PromptPreviewDialogProps) {
  const language = useLanguageStore((s) => s.language);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(finalPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex flex-col gap-4 w-full max-w-2xl max-h-[80vh] mx-4 bg-surface border border-surface-lighter rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-lighter">
          <h2 className="text-sm font-medium text-text">{t(language, 'previewTitle')}</h2>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4">
          <pre className="text-xs text-text-muted whitespace-pre-wrap break-words font-mono bg-black/30 p-3 rounded">
            {finalPrompt}
          </pre>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-surface-lighter">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-3 py-1.5 rounded bg-accent/20 text-accent hover:bg-accent/30 text-xs font-medium transition-colors"
          >
            {copied ? (
              <>
                <Check size={14} />
                <span>{t(language, 'copied')}</span>
              </>
            ) : (
              <>
                <Copy size={14} />
                <span>{t(language, 'copyPrompt')}</span>
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-1.5 rounded text-text-muted hover:text-text text-xs font-medium transition-colors"
          >
            {t(language, 'close')}
          </button>
        </div>
      </div>
    </div>
  );
}
